<template>
  <div class="module-container">
    <div class="risk-section">
      <div class="section-header">
        <div class="section-title">风险排查管理</div>
        <span class="more-link" @click="goToInspectionQuery">查看更多</span>
      </div>
      <div class="charts-row">
        <div class="chart-container">
          <div ref="chart1" class="chart"></div>
          <div class="chart-labels">
            <div class="label-item left-label-item">
              <span>执行总数量</span>
              <strong>{{ inspectionStats.totalExecutionCount }}</strong>
            </div>
            <div class="label-item left-label-item">
              <span>待执行数量</span>
              <strong class="pending">{{ inspectionStats.pendingExecutionCount }}</strong>
            </div>
            <div class="label-item left-label-item">
              <span>已执行数量</span>
              <strong class="completed">{{ inspectionStats.completedExecutionCount }}</strong>
            </div>
          </div>
        </div>
        <div class="chart-container">
          <div ref="chart2" class="chart"></div>
          <div class="chart-labels">
            <div class="label-item">
              <span>合格总数量</span>
              <strong>{{ inspectionStats.totalQualifiedCount }}</strong>
            </div>
            <div class="label-item">
              <span>待整改数量</span>
              <strong class="pending-governance">{{ inspectionStats.pendingRectificationCount }}</strong>
            </div>
            <div class="label-item">
              <span>已整改数量</span>
              <strong class="completed-governance">{{ inspectionStats.completedRectificationCount }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="risk-section">
      <div class="section-header">
        <div class="section-title">风险治理管理</div>
        <span class="more-link" @click="goToGovernanceQuery">查看更多</span>
      </div>
      <div class="charts-row">
        <div class="chart-container">
          <div ref="chart3" class="chart"></div>
          <div class="chart-labels">
            <div class="label-item left-label-item">
              <span>执行总数量</span>
              <strong>{{ governanceStats.totalExecutionCount }}</strong>
            </div>
            <div class="label-item left-label-item">
              <span>待执行数量</span>
              <strong class="pending">{{ governanceStats.pendingExecutionCount }}</strong>
            </div>
            <div class="label-item left-label-item">
              <span>已执行数量</span>
              <strong class="completed">{{ governanceStats.completedExecutionCount }}</strong>
            </div>
          </div>
        </div>
        <div class="chart-container">
          <div ref="chart4" class="chart"></div>
          <div class="chart-labels">
            <div class="label-item">
              <span>合格总数量</span>
              <strong>{{ governanceStats.totalQualifiedCount }}</strong>
            </div>
            <div class="label-item">
              <span>待整改数量</span>
              <strong class="pending-governance">{{ governanceStats.pendingRectificationCount }}</strong>
            </div>
            <div class="label-item">
              <span>已整改数量</span>
              <strong class="completed-governance">{{ governanceStats.completedRectificationCount }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="risk-section">
      <div class="section-header">
        <div class="section-title">风险设备检验</div>
        <span class="more-link" @click="goToDeviceInspectionQuery">查看更多</span>
      </div>
      <div class="charts-row single">
        <div class="chart-container">
          <div ref="chart5" class="chart"></div>
          <div class="chart-labels">
            <div class="label-item label-item-single">
              <span>检验设备数量</span>
              <strong>{{ equipmentInspectionStats.totalCount }}</strong>
            </div>
            <div class="label-item label-item-single">
              <span>已检验设备数量</span>
              <strong class="completed-inspection">{{ equipmentInspectionStats.executedCount }}</strong>
            </div>
            <div class="label-item label-item-single">
              <span>待检验设备数量</span>
              <strong class="pending-inspection">{{ equipmentInspectionStats.unexecutedCount }}</strong>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import { getGovernanceStatistics } from '@/api/safetyPrevention/riskGovernanceMan'
import { getInspectionExecutionStatistics } from '@/api/safetyPrevention/riskInspectionMan'
import { getRiskEquipmentInspectionStatistics } from '@/api/safetyPrevention/riskDeviceInspection'
import { addDate, getCurTimeYMD } from '@/utils/index'
export default {
  name: 'RiskManagement',
  data() {
    return {
      charts: [],
      inspectionStats: {
        totalExecutionCount: 0,
        pendingExecutionCount: 0,
        completedExecutionCount: 0,
        totalQualifiedCount: 0,
        pendingRectificationCount: 0,
        completedRectificationCount: 0
      },
      governanceStats: {
        totalExecutionCount: 0,
        pendingExecutionCount: 0,
        completedExecutionCount: 0,
        totalQualifiedCount: 0,
        pendingRectificationCount: 0,
        completedRectificationCount: 0
      },
      equipmentInspectionStats: {
        totalCount: 0,
        executedCount: 0,
        unexecutedCount: 0
      },
      refreshInterval: null
    }
  },
  mounted() {
    this.fetchAllStatistics()
    window.addEventListener('resize', this.handleResize)
    // 设置定时刷新，每30分钟刷新一次
    this.refreshInterval = setInterval(this.fetchAllStatistics, 1800000)
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    this.charts.forEach(chart => chart.dispose())
    // 清除定时器，避免内存泄漏
    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }
  },
  methods: {
    fetchAllStatistics() {
      this.fetchInspectionStatistics()
      this.fetchGovernanceStatistics()
      this.fetchEquipmentInspectionStatistics()
    },
    async fetchInspectionStatistics() {
      // 先清空数据，确保即使数据不变也能触发重新渲染
      this.inspectionStats = {
        totalExecutionCount: 0,
        pendingExecutionCount: 0,
        completedExecutionCount: 0,
        totalQualifiedCount: 0,
        pendingRectificationCount: 0,
        completedRectificationCount: 0
      }
      try {
        const postData = {
          projectId: JSON.parse(window.localStorage.userInfo).projectId,
          startDate: addDate(getCurTimeYMD(), -7),
          endDate: getCurTimeYMD()
        }
        const { data } = await getInspectionExecutionStatistics(postData)
        if (data) {
          this.inspectionStats = data
        }
      } finally {
        this.initCharts()
      }
    },
    async fetchGovernanceStatistics() {
      // 先清空数据，确保即使数据不变也能触发重新渲染
      this.governanceStats = {
        totalExecutionCount: 0,
        pendingExecutionCount: 0,
        completedExecutionCount: 0,
        totalQualifiedCount: 0,
        pendingRectificationCount: 0,
        completedRectificationCount: 0
      }
      try {
        const postData = {
          projectId: JSON.parse(window.localStorage.userInfo).projectId,
          startDate: addDate(getCurTimeYMD(), -7),
          endDate: getCurTimeYMD()
        }
        const { data } = await getGovernanceStatistics(postData)
        if (data) {
          this.governanceStats = data
        }
      } finally {
        this.initCharts()
      }
    },
    async fetchEquipmentInspectionStatistics() {
      // 先清空数据，确保即使数据不变也能触发重新渲染
      this.equipmentInspectionStats = {
        totalCount: 0,
        executedCount: 0,
        unexecutedCount: 0
      }
      try {
        const postData = {
          projectId: JSON.parse(window.localStorage.userInfo).projectId
        }
        const { data } = await getRiskEquipmentInspectionStatistics(postData)
        if (data) {
          this.equipmentInspectionStats = data
        }
      } finally {
        this.initCharts()
      }
    },
    initCharts() {
      this.createProgressChart(this.$refs.chart1, this.inspectionStats.totalExecutionCount, this.inspectionStats.completedExecutionCount, ['#01A2FB', '#65FBFF'])
      this.createProgressChart(this.$refs.chart2, this.inspectionStats.totalQualifiedCount, this.inspectionStats.completedRectificationCount, ['#B5FFDE', '#03D28A'])
      this.createProgressChart(this.$refs.chart3, this.governanceStats.totalExecutionCount, this.governanceStats.completedExecutionCount, ['#01A2FB', '#65FBFF'])
      this.createProgressChart(this.$refs.chart4, this.governanceStats.totalQualifiedCount, this.governanceStats.completedRectificationCount, ['#B5FFDE', '#03D28A'])
      this.createProgressChart(this.$refs.chart5, this.equipmentInspectionStats.totalCount, this.equipmentInspectionStats.executedCount, ['#FC8D04', '#FFEFA6'])
    },
    createProgressChart(el, total, completed, colors) {
      if (!el) return
      const chart = echarts.init(el)
      this.charts.push(chart)

      const percent = Math.round((completed / total) * 100)

      const option = {
        series: [
          {
            type: 'pie',
            radius: ['70%', '90%'],
            center: ['50%', '50%'],
            startAngle: 90,
            labelLine: {
              show: false
            },
            label: {
              show: true,
              position: 'center',
              formatter: function () {
                return '{value|' + total + '}'
              },
              rich: {
                value: {
                  fontSize: 18,
                  fontWeight: 'bold',
                  color: '#fff',
                  lineHeight: 18
                },
              }
            },
            data: [
              {
                value: completed,
                itemStyle: {
                  color: colors[0],
                  // shadowColor: colors[0]
                }
              },
              {
                value: total - completed,
                itemStyle: {
                  color: colors[1],
                }
              }
            ],
            emphasis: {
              scale: false
            }
          }
        ]
      }

      chart.setOption(option)
    },
    handleResize() {
      this.charts.forEach(chart => chart.resize())
    },
    goToInspectionQuery() {
      // 获取用户权限
      const userRoles = JSON.parse(decodeURI(window.localStorage.userRoles));
      // 计算日期参数（最近7天）
      const endDate = getCurTimeYMD()
      const startDate = addDate(endDate, -7)
      // 检查是否有风险排查管理权限
      if (userRoles && userRoles.includes('pre_paicharenwuchaxun')) {
        // 有权限，跳转到风险排查管理
        this.$router.push({
          path: '/safetyPrevention/riskInspectionMan/inspectionTaskQuery',
          query: {
            startDate,
            endDate
          }
        });
      } else {
        // 无权限，提示
        this.$message({
          type: 'warning',
          message: '您暂无权限访问排查任务查询'
        });
      }
    },
    goToGovernanceQuery() {
      // 获取用户权限
      const userRoles = JSON.parse(decodeURI(window.localStorage.userRoles));
      // 计算日期参数（最近7天）
      const endDate = getCurTimeYMD()
      const startDate = addDate(endDate, -7)
      // 检查是否有抽检任务查询权限
      if (userRoles && userRoles.includes('pre_choujianrenwuchaxun')) {
        // 有权限，跳转到抽检任务查询
        this.$router.push({
          path: '/safetyPrevention/riskGovernanceMan/samplingTaskQuery',
          query: {
            startDate,
            endDate
          }
        });
      } else {
        // 无权限，提示
        this.$message({
          type: 'warning',
          message: '您暂无权限访问抽检任务管理'
        });
      }
    },
    goToDeviceInspectionQuery() {
      // 获取用户权限
      const userRoles = JSON.parse(decodeURI(window.localStorage.userRoles));
      // 计算日期参数（最近7天）
      const endDate = getCurTimeYMD()
      const startDate = addDate(endDate, -7)

      // 检查是否有检验信息查询权限
      if (userRoles && userRoles.includes('pre_jianyanxinxichaxun')) {
        // 有权限，跳转到检验信息查询
        this.$router.push({
          path: '/safetyPrevention/riskDeviceInspection/inspectionQuery',
          query: {
            startDate,
            endDate
          }
        });
        
      } else {
        // 无权限，提示
        this.$message({
          type: 'warning',
          message: '您暂无权限访问检验信息查询'
        });
      }
    }
  }
}
</script>

<style scoped lang="scss">
.module-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.risk-section {
  width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.section-title {
  font-family: YouSheBiaoTiHei;
  font-weight: 400;
  font-size: 19px;
  color: #fff;
}

.more-link {
  font-size: 12px;
  color: #fff;
  cursor: pointer;
  transition: all 0.3s;
}

.more-link:hover {
  // 显示下划线
  text-decoration: underline;
}

.charts-row {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 5px;
  flex: 1;
  position: relative;
}

.charts-row:not(.single)::before {
  content: '';
  height: 60%;
  position: absolute;
  left: calc(50% - 17px);
  top: 20%;
  bottom: 10%;
  width: 2px;
  background: linear-gradient(to bottom, rgba(1, 162, 251, 0), rgba(1, 162, 251, 0.8), rgba(1, 162, 251, 0));
}

.charts-row.single {
  grid-template-columns: 1fr;
  max-width: 50%;
  margin: 0 auto;

  .chart {
    width: 120px;
    height: 120px;
  }
}

.chart-container {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 15px;
  height: 100px;
}

.chart {
  width: 80px;
  height: 80px;
}

.chart-labels {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
}

.label-item span {
  width: 60px;
  color: #fff;
}

.label-item-single span {
  width: 90px;
  color: #fff;
}

.label-item strong {
  width: 76px;
  text-align: left;
  color: #fff;
  font-size: 16px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.left-label-item {
  justify-content: flex-start;

  span {
    width: 60px;
    margin-right: 15px;
  }

  strong {
    width: 45px;
  }
}



.label-item strong.pending {
  color: #01A2FB;
}

.label-item strong.completed {
  color: #65FBFF;
}

.label-item strong.pending-governance {
  color: #03D28A;
}

.label-item strong.completed-governance {
  color: #B5FFDE;
}

.label-item strong.pending-inspection {
  color: #FFEEA5;
}

.label-item strong.completed-inspection {
  color: #FC8D03;
}
</style>
